/* global Node */
/**
 * Represents relation between two nodes.
 *
 * It's a number for performance reasons as it's used in sorting
 * functions for comparisons.
 */
export enum NodeRelation {
  /** It's the same DOM node */
  Identical,
  /** Node is inside */
  Inside,
  /** Node is a parent (not necessarily direct) */
  Parent,
  /** Node is before */
  Before,
  /** Node is after */
  After,
  /** No relation */
  Disconnected,
}

/**
 * Finds `NodeRelation` of `query` node in relation to `base` node,
 * for example if `query` is a child to `base` than it will return
 * `NodeRelation.Inside`
 */
export function getNodeRelation(query: Node, base: Node): NodeRelation {
  if (base === query) return NodeRelation.Identical;

  const rel = base.compareDocumentPosition(query);

  /* eslint-disable no-bitwise */
  return (
    rel & Node.DOCUMENT_POSITION_DISCONNECTED ? NodeRelation.Disconnected :
    rel & Node.DOCUMENT_POSITION_CONTAINED_BY ? NodeRelation.Inside :
    rel & Node.DOCUMENT_POSITION_CONTAINS     ? NodeRelation.Parent :
    rel & Node.DOCUMENT_POSITION_PRECEDING    ? NodeRelation.Before :
    rel & Node.DOCUMENT_POSITION_FOLLOWING    ? NodeRelation.After  :
    // Treat Node.DOCUMENT_POSITION_IMPLEMENTATION_SPECIFIC as disconnected
    NodeRelation.Disconnected
  );
  /* eslint-enable no-bitwise */
}

/**
 * Finds position of `node` in a list of `nodes`. Assumes `nodes`
 * is sorted in DOM order (with `compareNodePosition`).
 *
 * - If the `node` itself or its child is found, it will
 *   return `{ match: number }`.
 * - If the `node` is before first element on the list, it returns
 *   `{ before: 0 }`.
 * - If the `node` is after all element on the list it returns
 *   `{ after: index of last element }`.
 * - If the `node` itself is not on the list but it's between two,
 *   it'll return `{ after: someIndex, before: someIndex + 1 }`
 * - Otherwise it'll return `{}`, for example if `node` is a parent
 *   of either first or last elements on the list
 */
export function binarySearchPosition(nodes: ReadonlyArray<{ node: Node }>, node: Node): {
  before?: number,
  match?: number,
  after?: number,
} {
  if (nodes.length === 0) return {};

  if (nodes.length === 1) {
    const nodeRelation = getNodeRelation(node, nodes[0].node);

    if (isInside(nodeRelation)) return { match: 0 };
    if (nodeRelation === NodeRelation.Before) return { before: 0 };
    if (nodeRelation === NodeRelation.After) return { after: 0 };
  }

  let left = 0;
  let right = nodes.length - 2;

  while (left <= right) {
    const middle = Math.floor((left + right) / 2);

    const relationLeft = getNodeRelation(node, nodes[middle].node);
    const relationRight = getNodeRelation(node, nodes[middle + 1].node);

    if (relationLeft === NodeRelation.Before) {
      right = middle - 1;
    } else if (isInside(relationLeft)) {
      return { match: middle };
    } else if (
      relationLeft === NodeRelation.After &&
      relationRight === NodeRelation.Before
    ) {
      // The looked-for element is between two nodes on the list
      // but not on the list itself
      return { after: middle, before: middle + 1 };
    } else if (isInside(relationRight)) {
      return { match: middle + 1 };
    } else if (relationRight === NodeRelation.After) {
      left = middle + 1;
    } else {
      return {};
    }
  }

  return left === 0 ? { before: 0 } : { after: left };
}

export function isInside(relation: NodeRelation) {
  return relation === NodeRelation.Identical || relation === NodeRelation.Inside;
}

export function compareNodePosition(a: Node, b: Node): -1 | 0 | 1 {
  const relation = getNodeRelation(a, b);
  return (
    relation === NodeRelation.Before ? -1 :
    relation === NodeRelation.After ? 1 :
    0
  );
}

/**
 * Finds length of the text inside `container` before `node`
 */
export function findTextOffset(container: Node, node: Node): number {
  const relation = getNodeRelation(node, container);

  if (relation === NodeRelation.After) return -1;
  // The recursion will stop here if we reach `NodeRelation.Identical`
  if (relation !== NodeRelation.Inside) return 0;

  let sum = 0;
  for (let sibling = node.previousSibling; sibling; sibling = sibling.previousSibling) {
    sum += textLength(sibling);
  }

  const parent = node.parentNode;
  if (parent) {
    // recurse up reaching the container
    return sum + findTextOffset(container, parent);
  } else {
    return sum;
  }
}

/**
 * Finds sum of text lengths of all text nodes inside `node`
 */
export function textLength(node: Node): number {
  let sum = 0;

  for (const text of nodeTexts(node)) {
    sum += text.length;
  }

  return sum;
}

/**
 * Finds text node inside `container` that contains text at `offset`.
 * Returns `{ node, offset }` or `null` which is the found `node`
 * and `offset` local to the `node`.
 */
export function findNodeAtOffset(container: Node, offset: number): {
  node: Node,
  offset: number,
} | null {
  if (offset === -1) {
    const result = textNodesFromBack(container).next();

    if (result.done) {
      return null;
    } else {
      const node = result.value;
      return { node: result.value, offset: textLength(node) };
    }
  }

  let offsetLeft = offset;

  for (const node of textNodes(container)) {
    const { length } = node.nodeValue || '';

    if (offsetLeft - length <= 0) {
      return { node, offset: offsetLeft };
    }

    offsetLeft -= length;
  }

  return null;
}

/**
 * Yields all text nodes inside a DOM node
 */
export function* textNodes(node: Node | null): IterableIterator<Node> {
  if (node) {
    switch (node.nodeType) {
      case Node.TEXT_NODE:
        yield node;
        break;
      case Node.ELEMENT_NODE:
        for (let child: Node | null = node.firstChild; child; child = child.nextSibling) {
          yield* textNodes(child);
        }
        break;
      default:
        break;
    }
  }
}

export function* textNodesFromBack(node: Node | null): IterableIterator<Node> {
  if (node) {
    switch (node.nodeType) {
      case Node.TEXT_NODE:
        yield node;
        break;
      case Node.ELEMENT_NODE:
        for (let child: Node | null = node.lastChild; child; child = child.previousSibling) {
          yield* textNodesFromBack(child);
        }
        break;
      default:
        break;
    }
  }
}

function* extractTexts(nodes: IterableIterator<Node>): IterableIterator<string> {
  for (const child of nodes) {
    yield child.nodeValue || '';
  }
}

export function nodeTexts(node: Node): IterableIterator<string> {
  return extractTexts(textNodes(node));
}

export function nodeTextsFromBack(node: Node): IterableIterator<string> {
  return extractTexts(textNodesFromBack(node));
}

function whiteSpaceLength(texts: Iterable<string>, rex: RegExp): number {
  let length = 0;

  for (const text of texts) {
    const match = text.match(rex);

    if (!match) break;

    length += match[0].length;
  }

  return length;
}

export function leftWhiteSpaceLength(node: Node): number {
  return whiteSpaceLength(nodeTexts(node), /^\s+/);
}

export function rightWhiteSpaceLength(node: Node): number {
  return whiteSpaceLength(nodeTextsFromBack(node), /\s+$/);
}
